/* eslint-disable import/no-unused-modules */
import { map, indexOf, isNil, filter, isEqual } from 'lodash';
import { apiCallStatus, allAssetTypes } from 'helpers/constants';
import { toggleButtonOptions, pageSize, conditionIndices, conditionPriority, getTabOptionNames, conditionIndicesViewOptions, assetDataKeys } from './constants';
import localStorageHelper from 'helpers/localStorageHelper';

export const getToggleButtonOptions = (translate) => [

    { label: translate('ABB.Powertrain.Frontend.powertrainsLabel'), value: toggleButtonOptions.POWERTRAINS },
    { label: translate('ABB.Powertrain.Frontend.assetGroupsLabel'), value: toggleButtonOptions.ASSETGROUPS },
    { label: translate('ABB.Powertrain.Frontend.assetsLabel'), value: toggleButtonOptions.ASSETS }
];

export const setFilterVisibilityToLocalStorage = (visibility) => {
    localStorageHelper.setItemForUser('assetPowertrainOverviewFilterVisibility', visibility);
};

export const getFilterVisibilityFromLocalStorage = () => {
    const localStorageExpandedStatus = localStorageHelper.getItemForUser('assetPowertrainOverviewFilterVisibility');
    return localStorageExpandedStatus === null ? true : localStorageHelper.getItemForUser('assetPowertrainOverviewFilterVisibility');
};

export const setSelectedConditions = (selectedConditions, newCondition) => {
    const isSelected = indexOf(selectedConditions, newCondition);
    if (isSelected >= 0) {
        // Return a new array with the item removed
        return filter(selectedConditions, (_, index) => index !== isSelected);
    }
    return [...selectedConditions, newCondition];
};

export const setSelectedSubscriptionTypes = (SubscriptionTypes) => {
    const getSubscriptionType = map(SubscriptionTypes, 'value');
    return getSubscriptionType;
};

export const addNewPageToConditionIndicesOverviewList = (items, action) => {
    const data = action.loadingStatus !== apiCallStatus.LOADING &&
        action.data?.pageNumber === 1 ? [] : [...items.data];

    return action.loadingStatus !== apiCallStatus.SUCCESS ? {
        ...items,
        loadingStatus: action.loadingStatus,
        initialLoadDone: true
    } : {
        ...items,
        loadingStatus: action.loadingStatus,
        count: action.data.count,
        ...!isNil(action.data.assetCount) && { assetCount: action.data.assetCount },
        data: action.data && [...data, ...action.data?.items ? action.data.items : []],
        pageNumber: action.data.hasMore ? action.data?.pageNumber + 1 : action.data?.pageNumber,
        showThreshold: action?.data?.showThreshold,
        showTrend: action?.data?.showTrend,
        hasMore: action?.data?.hasMore,
        initialLoadDone: true
    };
};

const collectAssetIds = (assets, ids) => {
    (assets || []).forEach(a => ids.push(a.assetId));
};

const collectPowertrainAssetIds = (powertrains, ids) => {
    (powertrains || []).forEach(pt => collectAssetIds(pt?.assets, ids));
};

const addFromLocation = (location, ids) => {
    collectAssetIds(location?.assets, ids);
    collectPowertrainAssetIds(location?.powertrains, ids);
    (location?.subLocations || []).forEach(sub => addFromLocation(sub, ids));
};

const addFromOrg = (org, ids) => {
    if (!org) { return; }
    (org.sites || []).forEach(site => {
        collectAssetIds(site.notConfiguredAssets, ids);
        collectPowertrainAssetIds(site.notConfiguredPowertrains, ids);
        (site.locations || []).forEach(loc => addFromLocation(loc, ids));
    });
    (org.subOrganizations || []).forEach(subOrg => addFromOrg(subOrg, ids));
};

const getHierarchyAssetCount = (hierarchyData) => {
    const ids = [];
    addFromOrg(hierarchyData, ids);
    return ids.length;
};

export const countHierarchyAssets = (hierarchyData) => getHierarchyAssetCount(hierarchyData);

const collectAssetObjects = (assets, collector) => {
    (assets || []).forEach(a => collector.push(a));
};

const collectPowertrainAssetObjects = (powertrains, collector) => {
    (powertrains || []).forEach(pt => collectAssetObjects(pt?.assets, collector));
};

const addAssetObjectsFromLocation = (location, collector) => {
    collectAssetObjects(location?.assets, collector);
    collectPowertrainAssetObjects(location?.powertrains, collector);
    (location?.subLocations || []).forEach(sub => addAssetObjectsFromLocation(sub, collector));
};

const addAssetObjectsFromOrg = (org, collector) => {
    if (!org) { return; }
    (org?.sites || []).forEach(site => {
        collectAssetObjects(site?.notConfiguredAssets, collector);
        collectPowertrainAssetObjects(site?.notConfiguredPowertrains, collector);
        (site?.locations || []).forEach(loc => addAssetObjectsFromLocation(loc, collector));
    });
    (org?.subOrganizations || []).forEach(subOrg => addAssetObjectsFromOrg(subOrg, collector));
};

// Same tree walk as countHierarchyAssets, but returns the actual asset objects instead of just ids.
export const collectHierarchyAssets = (hierarchyData) => {
    const assets = [];
    addAssetObjectsFromOrg(hierarchyData, assets);
    return assets;
};

export const hierarchyDataFormatter = (items, action) => {
    switch (action.loadingStatus) {
        case apiCallStatus.ERROR:
            return {
                ...items,
                initialLoadDone: true,
                loadingStatus: action.loadingStatus,
            };
        case apiCallStatus.SUCCESS:
            return {
                ...items,
                loadingStatus: action.loadingStatus,
                data: action?.data,
                initialLoadDone: true
            };
        default:
            return {
                ...items,
                loadingStatus: action.loadingStatus
            };
    }
};

export const getOverviewNumber = (
    selectedTab,
    assetNumber,
    assetGroupNumber,
    assetGroupAssetNumber,
    powertrainNumber,
    powertrainAssetNumber,
    translate
) => {
    switch (selectedTab) {
        case toggleButtonOptions.ASSETS: {
            return typeof assetNumber === 'number' &&
                `${assetNumber} ${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewAssetNumber')}`;
        }
        case toggleButtonOptions.ASSETGROUPS: {
            return typeof assetGroupNumber === 'number' &&
                `${assetGroupNumber} ${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewAssetGroupNumber')} | ${assetGroupAssetNumber} ${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewAssetNumber')} `;
        }
        case toggleButtonOptions.POWERTRAINS: {
            return typeof powertrainNumber === 'number' &&
                `${powertrainNumber} ${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewPowertrainNumber')} | ${powertrainAssetNumber} ${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewAssetNumber')} `;
        }
        default: return '';
    }
};

export const isTabContentLoading = (
    selectedTab,
    assetOverviewLoadingStatus,
    assetGroupOverviewLoadingStatus,
    powertrainOverviewLoadingStatus,
    hierarchyConditionIndicesLoadingStatus
) => {
    if (hierarchyConditionIndicesLoadingStatus === apiCallStatus.LOADING) {
        return true;
    }
    switch (selectedTab) {
        case toggleButtonOptions.ASSETS: {
            return assetOverviewLoadingStatus === apiCallStatus.LOADING;
        }
        case toggleButtonOptions.ASSETGROUPS: {
            return assetGroupOverviewLoadingStatus === apiCallStatus.LOADING;
        }
        case toggleButtonOptions.POWERTRAINS: {
            return powertrainOverviewLoadingStatus === apiCallStatus.LOADING;
        }
        default: return false;
    }
};

export const formatConditionIndexOverviewRequest = (
    pageNumber,
    selectedFilters,
    filter,
    conditionFilter,
    assetTypeFilter = '',
    subscriptionTypeFilter = [],
    sensorIdentifierFilter = '',
    refreshFlag = false
) => {
    const sensorIdentifiers = sensorIdentifierFilter
        ? sensorIdentifierFilter.split(',').map(item => item.trim()).filter(item => item.length > 0)
        : [];
    const payload = {
        pageNumber,
        pageSize,
        organizationIds: selectedFilters?.organizations?.length && selectedFilters?.organizations.map(organization => {
            return organization.organizationID;
        }) || [],
        siteIds: selectedFilters?.sitesChecked && selectedFilters?.sites?.length &&
            selectedFilters?.sites.map(site => {
                return site.siteID;
            }) || [],
        assetGroupIds: selectedFilters?.assetGroupChecked && selectedFilters?.assetGroups?.length &&
            selectedFilters?.assetGroups.map(assetGroup => {
                return assetGroup?.id;
            }) || [],
        conditionFilter,
        assetTypeFilter: assetTypeFilter === allAssetTypes ? '' : assetTypeFilter,
        subscriptionTypeFilter,
        sensorIdentifiers,
        sortingCriteria: ['OverallCondition'],
        sortingOrder: 'asc',
        refreshData: refreshFlag
    };
    if (filter) {
        const trimmedFilter = filter.trim();
        if (trimmedFilter.length > 0) {
            payload.filter = trimmedFilter;
        }
    }
    return payload;
};

export const filteredUnsupportedAssetData = (list) => {
    if (list?.length > 0) {
        return list.filter(item => item?.condition !== conditionIndices.UNSUPPORTED);
    }
    return [];
};

export const filteredUnsupportePowertrainData = (list) => {
    if (list?.length > 0) {
        return list.filter(item => item?.conditionIndexCondition !== conditionIndices.UNSUPPORTED);
    }
    return [];
};

export const filteredUnsupporteAssetGroupData = (list) => {
    if (list?.length > 0) {
        return list.filter(item => item?.conditionIndexCondition !== conditionIndices.UNSUPPORTED);
    }
    return [];
};

export const filteredPowertrain = (powertrainList, selectedPowertrainId) => {
    return filter(powertrainList, (powertrain) => {
        return selectedPowertrainId === powertrain?.powertrainId;
    });
};

export const filteredAssetGroup = (assetGroupList, selectedAssetGroupId) => {
    return filter(assetGroupList, (assetGroup) => {
        return selectedAssetGroupId === assetGroup?.assetGroupId;
    });
};

export const iconCondition = (condition) => {
    switch (condition) {
        case conditionIndices.GOOD: return 'condition-ok';
        case conditionIndices.TOLERABLE: return 'condition-tolerable';
        case conditionIndices.POOR: return 'condition-poor';
        case conditionIndices.UNSUPPORTED: return 'not-supported';
        default: return 'condition-unknown';
    }
};

export const updateAssetName = (assets, actionData) => {

    if (assets === null) { return assets; }

    const targetId = String(actionData?.id || '');

    if (Array.isArray(assets)) {
        return map(assets, item => updateAssetName(item, actionData));
    }

    if (typeof assets === 'object') {
        const currentId = String(assets.assetId || assets.id || '');
        const isTargetAsset = currentId === targetId && targetId !== '';

        const updated = {
            ...assets,
            ...isTargetAsset && {
                assetName: actionData?.name,
                name: actionData?.name
            }
        };

        Object.keys(updated).forEach(key => {
            const value = updated[key];
            if (Array.isArray(value)) {
                updated[key] = map(value, item => updateAssetName(item, actionData));
            } else if (value !== null && typeof value === 'object') {
                updated[key] = updateAssetName(value, actionData);
            }
        });

        return updated;
    }

    return assets;
};

export const updateGroupAssets = (groupData, actionData) => {

    if (Array.isArray(groupData)) {
        return map(groupData, group => ({
            ...group,
            assets: updateAssetName(group?.assets, actionData)
        }));
    }

    if (typeof groupData === 'object') {
        const updated = { ...groupData };
        Object.keys(updated).forEach(key => {
            if (key === assetDataKeys.ASSETS || key === assetDataKeys.NOT_CONFIGURED_ASSETS) {
                updated[key] = updateAssetName(updated[key], actionData);
            } else if (Array.isArray(updated[key])) {
                updated[key] = map(updated[key], item => updateGroupAssets(item, actionData));
            } else if (updated[key] && typeof updated[key] === 'object') {
                updated[key] = updateGroupAssets(updated[key], actionData);
            }
        });
        return updated;
    }

    return groupData;
};

export const sortBasedOnOveralCondition = (items) => {
    const getConditionPriority = (assets = []) => {
        if (assets.some(asset => asset.condition === conditionIndices.POOR)) { return conditionIndices.POOR; }
        if (assets.some(asset => asset.condition === conditionIndices.TOLERABLE)) { return conditionIndices.TOLERABLE; }
        if (assets.some(asset => asset.condition === conditionIndices.GOOD)) { return conditionIndices.GOOD; }
        return conditionIndices.UNKNOWN;
    };

    return items
        .map(item => ({
            ...item,
            overalCondition: getConditionPriority(item.assets)
        }))
        .sort((a, b) => conditionPriority[a.overalCondition] - conditionPriority[b.overalCondition]);
};

export const getTabKey = (selectedTab, conditionIndicesView) => {
    if (conditionIndicesView === conditionIndicesViewOptions.HIERARCHY) {
        return conditionIndicesViewOptions.HIERARCHY;
    }
    if (selectedTab === toggleButtonOptions.POWERTRAINS) {
        return getTabOptionNames.POWERTRAINS;
    }
    if (selectedTab === toggleButtonOptions.ASSETGROUPS) {
        return getTabOptionNames.ASSETGROUPS;
    }
    if (selectedTab === toggleButtonOptions.ASSETS) {
        return getTabOptionNames.ASSETS;
    }
    return null;
};

export const updateFilterProperty = (state, propertyName, propertyValue) => {
    const tabKey = getTabKey(state.selectedTab, state.conditionIndicesView);
    if (!tabKey) {
        return state.filter;
    }

    const currentValue = state.filter[tabKey]?.[propertyName];
    const valueChanged = !isEqual(currentValue, propertyValue);
    return {
        filter: {
            ...state.filter,
            [tabKey]: {
                ...state.filter[tabKey],
                [propertyName]: propertyValue
            }
        },
        changed: valueChanged
    };
};

export const resetInitialLoadDoneOnFilterChange = (state, filterChanged = true) => {
    if (!filterChanged) {
        return state;
    }

    const tabKey = getTabKey(state.selectedTab, state.conditionIndicesView);
    if (!tabKey) {
        return state;
    }

    // Only update the selected tab's initialLoadDone
    return {
        ...state,
        isFilterInProgress: true,
        [tabKey]: {
            ...state[tabKey],
            initialLoadDone: false
        }
    };
};

export const getTabSpecificFilter = (activeTab, assetsValue, assetGroupsValue, powertrainsValue, defaultValue = [], conditionIndicesView, hierarchyValue) => {
    if (conditionIndicesView === conditionIndicesViewOptions.HIERARCHY) {
        return hierarchyValue;
    }
    switch (activeTab) {
        case toggleButtonOptions.ASSETS:
            return assetsValue;
        case toggleButtonOptions.ASSETGROUPS:
            return assetGroupsValue;
        case toggleButtonOptions.POWERTRAINS:
            return powertrainsValue;
        default:
            return defaultValue;
    }
};

export const handleRemoveQueryParams = (history) => {
    if (history?.location?.search && history?.location?.search !== '') {
        return history.replace({
            search: '',
        });
    }
};
